import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";
import { ORG_TYPE_META, type OrgUnitRow } from "../components/hr/orgTypes";
import { computeShiftOff, flattenOrgTree } from "../components/hr/orgLayout";

/**
 * ORGANİZASYON ŞEMASI — yazdırılabilir A4 DİKEY belge.
 *
 * ── NEDEN SVG DEĞİL ──
 * Ekrandaki şema tek parça bir SVG. Tek parça SVG SAYFALARA BÖLÜNEMEZ: tarayıcı ya
 * hepsini tek sayfaya sıkıştırır (30 birimlik bir şemada yazı okunmaz olur) ya da
 * alt kısmını kırpar. Deponun diğer baskı belgeleriyle (vardiya planı, mal kabul
 * tutanağı) aynı ilke: "sayfaya sığdır" sorunu ÖLÇEKLEYEREK değil DOĞRU YERLEŞİMLE
 * çözülür — burada girintili HTML hiyerarşi, bağlantılar CSS kenarlığıyla çizilir.
 * Belge kaç sayfa sürerse sürsün yazı tam boyda ve vektör kalır.
 *
 * SIRA ekrandaki şemayla AYNIdır: ikisi de `orgLayout.ts`teki ağacı kullanır.
 *
 * Uygulama kabuğu (sidebar) DIŞINDADIR; yeni sekmede açılır (router.tsx /print/*).
 */
export function OrgChartPrintPage() {
  const [sp] = useSearchParams();

  const [optCompany, setOptCompany] = useState(true);
  const [optCode, setOptCode] = useState(true);
  const [optInactive, setOptInactive] = useState(false); // pasif birimler varsayılan HARİÇ
  const [optShift, setOptShift] = useState(false);       // "vardiya dışı" işareti
  const [tesis, setTesis] = useState<string>(sp.get("tesisId") ?? "");

  const listQ = trpc.hr.orgUnit.list.useQuery(undefined, { staleTime: 30_000 });
  const prof = trpc.settings.companyProfile.get.useQuery({}, { staleTime: 5 * 60_000 });
  const co = prof.data;

  const all = useMemo(() => (listQ.data ?? []) as OrgUnitRow[], [listQ.data]);

  /** Tesis süzgeci — birden çok tesis varsa tek tesisin şeması basılabilsin. */
  const tesisler = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of all) if (u.tesisId && u.tesisName) m.set(u.tesisId, u.tesisName);
    return [...m].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, "tr"));
  }, [all]);

  /**
   * Süzgeçlenmiş küme. DİKKAT: bir birim elenince ALT AĞACI da elenmelidir; yoksa
   * `orgLayout` onları "öksüz kök" sayıp belgeye ayrı kök olarak basar ve şema
   * pasif bir birimin altındaki ekipleri en üstte gösterirdi.
   */
  const units = useMemo(() => {
    const kept = all.filter((u) => (optInactive || u.isActive) && (!tesis || u.tesisId === tesis));
    const keptIds = new Set(kept.map((u) => u.id));
    const droppedPaths = all.filter((u) => !keptIds.has(u.id)).map((u) => u.path);
    return kept.filter((u) => !droppedPaths.some((p) => u.path.startsWith(p)));
  }, [all, optInactive, tesis]);

  const rows = useMemo(() => flattenOrgTree(units), [units]);
  // MİRASLI "vardiya dışı" — ekrandaki ağaçla AYNI fonksiyon. Yalnız birimin kendi bayrağına
  // bakmak, üstü kapalı alt birimi belgede "planda var" gibi göstermişti; belgenin kendi
  // açıklaması da o satır için yanlış oluyordu.
  const shiftOff = useMemo(() => computeShiftOff(units), [units]);
  const gizlenen = all.length - units.length;

  const today = new Date();
  const nowLabel = `${String(today.getDate()).padStart(2, "0")}.${String(today.getMonth() + 1).padStart(2, "0")}.${today.getFullYear()}`;
  const kapsam = tesis ? (tesisler.find((t) => t.id === tesis)?.name ?? "") : "Tüm tesisler";

  return (
    <div className="ocp-root">
      <style>{`
        .ocp-root { --ink:#14253E; --line:#d7dce5; --muted:#5b6472; --soft:#f2f5fa; background:#fff; color:#1a2230; }
        .ocp-page { padding: 14px 18px 30px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 11px; }

        .ocp-head { display:flex; align-items:flex-start; justify-content:space-between; gap:16px;
                    border-bottom: 2px solid var(--ink); padding-bottom: 8px; margin-bottom: 14px; }
        .ocp-title { font-size: 16px; font-weight: 800; color: var(--ink); letter-spacing:-0.01em; }
        .ocp-scope { font-size: 12px; font-weight: 700; color: var(--ink); margin-top: 2px; }
        .ocp-sub { font-size: 10.5px; color: var(--muted); margin-top: 3px; }
        .ocp-co { text-align:right; font-size: 10.5px; color: var(--muted); }
        .ocp-co b { color: var(--ink); font-size: 12.5px; display:block; }
        .ocp-logo { max-height: 44px; max-width: 120px; object-fit: contain; margin-bottom: 4px; }

        /* ── HİYERARŞİ ────────────────────────────────────────────────────────────
           Her satır bir birim; girinti seviyeyi, kenarlıklar bağlantıyı gösterir.
           Bağlantı çizgileri ATA BAŞINA ayrı bir oluk (span) ile çizilir: bir ata SON
           çocuksa onun altındaki satırlarda dikey çizgi DEVAM ETMEMELİ. Tek bir
           kenarlıkla bunu ifade etmek mümkün değil — hangi seviyenin çizgisinin
           süreceği satır satır değişiyor. */
        .ocp-row { display:flex; align-items:stretch; break-inside: avoid; }
        .ocp-gut { width: 16px; flex:none; position:relative; }
        .ocp-gut.line::before { content:""; position:absolute; left:7px; top:0; bottom:0; border-left:1px solid var(--line); }
        .ocp-gut.tee::before  { content:""; position:absolute; left:7px; top:0; bottom:0; border-left:1px solid var(--line); }
        .ocp-gut.tee::after   { content:""; position:absolute; left:7px; top:50%; width:9px; border-top:1px solid var(--line); }
        .ocp-gut.end::before  { content:""; position:absolute; left:7px; top:0; height:50%; border-left:1px solid var(--line); }
        .ocp-gut.end::after   { content:""; position:absolute; left:7px; top:50%; width:9px; border-top:1px solid var(--line); }

        .ocp-node { flex:1; min-width:0; display:flex; align-items:center; gap:8px;
                    border:1px solid var(--line); border-radius:5px; background:#fff;
                    padding: 5px 9px; margin: 2px 0; }
        .ocp-node.lv0 { background: var(--ink); border-color: var(--ink); }
        .ocp-node.lv0 .nm, .ocp-node.lv0 .ty, .ocp-node.lv0 .cd, .ocp-node.lv0 .ts { color:#fff; }
        .ocp-node.lv1 { background: var(--soft); }
        .ocp-node.off { border-style: dashed; }
        .ocp-node .nm { font-size: 11.5px; font-weight: 700; color: var(--ink); overflow-wrap:anywhere; }
        .ocp-node .ty { font-size: 9.5px; color: var(--muted); white-space:nowrap; }
        .ocp-node .cd { font-family:'JetBrains Mono', monospace; font-size: 9px; color: var(--muted); white-space:nowrap; }
        .ocp-node .ts { font-size: 9px; color: var(--muted); margin-left:auto; white-space:nowrap; }
        .ocp-node .fl { font-size: 9px; color:#8a5a00; border:1px solid #e6cf9a; background:#fdf7e8;
                        border-radius:3px; padding:0 4px; white-space:nowrap; }

        .ocp-legend { margin-top: 14px; padding-top: 7px; border-top: 1px solid var(--line);
                      font-size: 9.5px; color: var(--muted); display:flex; gap:14px; flex-wrap:wrap; }
        .ocp-foot { margin-top: 8px; font-size: 9.5px; color: var(--muted); display:flex; justify-content:space-between; }
        .ocp-empty { padding: 40px; text-align:center; color: var(--muted); }

        .ocp-toolbar { position: sticky; top:0; z-index:5; background:#fff; display:flex; gap:10px;
                       align-items:center; justify-content:space-between; padding: 9px 18px; border-bottom:1px solid var(--line); }
        .ocp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px;
                   padding:7px 16px; border-radius:6px; cursor:pointer; font-family:inherit; }
        .ocp-btn.sec { background:#fff; color: var(--ink); }
        .ocp-opts { display:flex; align-items:center; gap:12px; flex-wrap:wrap; font-size:11.5px; color:#1a2230; }
        .ocp-optlbl { font-size:10px; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .ocp-opts label { display:inline-flex; align-items:center; gap:5px; cursor:pointer; white-space:nowrap; }
        .ocp-opts input { width:14px; height:14px; accent-color: var(--ink); cursor:pointer; }
        .ocp-opts select { font-family:inherit; font-size:11.5px; padding:3px 6px; border:1px solid var(--line); border-radius:5px; }
        .ocp-tb-actions { display:flex; gap:8px; flex-shrink:0; }

        @media print {
          .ocp-toolbar { display:none !important; }
          .ocp-page { padding: 0; }
          .ocp-row { break-inside: avoid; }
          /* Bir birim başlığı sayfanın son satırı olarak yalnız kalmasın. */
          .ocp-node.lv0, .ocp-node.lv1 { break-after: avoid; }
          .ocp-root, .ocp-root * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
        @page { size: A4 portrait; margin: 10mm; }
      `}</style>

      <div className="ocp-toolbar">
        <div className="ocp-opts">
          <span className="ocp-optlbl">Belgede yer alsın:</span>
          <label><input type="checkbox" checked={optCompany} onChange={(e) => setOptCompany(e.target.checked)} />Firma künyesi</label>
          <label><input type="checkbox" checked={optCode} onChange={(e) => setOptCode(e.target.checked)} />Birim kodu</label>
          <label><input type="checkbox" checked={optInactive} onChange={(e) => setOptInactive(e.target.checked)} />Pasif birimler</label>
          <label><input type="checkbox" checked={optShift} onChange={(e) => setOptShift(e.target.checked)} />Vardiya dışı işareti</label>
          {tesisler.length > 1 ? (
            <label>
              Tesis:
              <select value={tesis} onChange={(e) => setTesis(e.target.value)}>
                <option value="">Tümü</option>
                {tesisler.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          ) : null}
        </div>
        <div className="ocp-tb-actions">
          <button type="button" className="ocp-btn sec" onClick={() => window.close()}>Kapat</button>
          <button type="button" className="ocp-btn" onClick={() => window.print()} disabled={rows.length === 0}>Yazdır / PDF</button>
        </div>
      </div>

      <div className="ocp-page">
        {listQ.isLoading ? (
          <div className="ocp-empty">Yükleniyor…</div>
        ) : listQ.error ? (
          <div className="ocp-empty">{listQ.error.message}</div>
        ) : rows.length === 0 ? (
          <div className="ocp-empty">Bu seçimde gösterilecek birim yok.</div>
        ) : (
          <>
            <div className="ocp-head">
              <div>
                <div className="ocp-title">ORGANİZASYON ŞEMASI</div>
                <div className="ocp-scope">{kapsam}</div>
                <div className="ocp-sub">
                  {rows.length} birim
                  {gizlenen > 0 ? ` · ${gizlenen} birim süzgeç dışında bırakıldı` : ""}
                </div>
              </div>
              <div className="ocp-co">
                {optCompany ? (
                  <>
                    {co?.logoUrl ? <img className="ocp-logo" src={co.logoUrl} alt="" /> : null}
                    <b>{co?.name ?? ""}</b>
                    {co?.address ? <div>{co.address}</div> : null}
                  </>
                ) : null}
                <div>Yazdırma: {nowLabel}</div>
              </div>
            </div>

            {rows.map(({ u, level, isLast, ancestorsLast }) => {
              const meta = ORG_TYPE_META[u.type];
              return (
                <div className="ocp-row" key={u.id}>
                  {/* Ata sütunları: ata SON çocuksa altında çizgi DEVAM ETMEZ. */}
                  {ancestorsLast.map((last, i) => <span key={i} className={`ocp-gut${last ? "" : " line"}`} aria-hidden="true" />)}
                  {level > 0 ? <span className={`ocp-gut ${isLast ? "end" : "tee"}`} aria-hidden="true" /> : null}
                  <div className={`ocp-node lv${Math.min(level, 2)}${u.isActive ? "" : " off"}`}>
                    <span className="nm">{u.name}</span>
                    <span className="ty">{meta.label}</span>
                    {optCode && u.code ? <span className="cd">{u.code}</span> : null}
                    {!u.isActive ? <span className="fl">Pasif</span> : null}
                    {optShift && shiftOff.has(u.id)
                      ? <span className="fl">{shiftOff.get(u.id) === "self" ? "Vardiya dışı" : "Vardiya dışı (üstten)"}</span>
                      : null}
                    {!tesis ? <span className="ts">{u.tesisName ?? "Global"}</span> : null}
                  </div>
                </div>
              );
            })}

            <div className="ocp-legend">
              <span>Girinti = organizasyon seviyesi</span>
              {optInactive ? <span>Kesik çerçeve = pasif birim</span> : null}
              {optShift ? <span>“Vardiya dışı” = birim ve ALT BİRİMLERİ vardiya planına girmez · “(üstten)” = üst birimi kapalı olduğu için</span> : null}
            </div>
            <div className="ocp-foot">
              <span>{co?.name ?? "STEPERP"}</span>
              <span>Organizasyon Şeması · {kapsam} · {nowLabel}</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
